import React from 'react';
import { motion } from 'framer-motion';
import { 
  Rocket, 
  Settings, 
  Users, 
  Zap, 
  Calendar,
  CheckCircle2,
  Clock
} from 'lucide-react';

const FrontOfficeGettingStartedSlide = () => {
  return (
    <div className="min-h-full xl:h-full w-full bg-slate-50 flex items-center justify-center p-4 xl:p-12 relative overflow-y-auto xl:overflow-hidden text-slate-900">
      {/* Background Ambience */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
         <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-100/50 rounded-full blur-[120px]" />
         <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-100/50 rounded-full blur-[120px]" />
      </div>

      <div className="max-w-[1400px] w-full z-10 flex flex-col h-full justify-center">
        
        {/* Header */}
        <motion.div 
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
        >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-semibold mb-6 shadow-sm">
                <Rocket className="w-4 h-4" />
                <span>Front Office Launch</span>
            </div>
            <h2 className="text-4xl lg:text-6xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-slate-900 to-blue-800">
                Getting started is simple.
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
                We handle the heavy lifting to get your AI team live in just 24 hours.
            </p>
        </motion.div>

        {/* Timeline Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20 relative">
            {/* Connector Line */}
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-gradient-to-r from-blue-200/50 via-indigo-200/50 to-blue-200/50 z-0" />

            {/* Step 1: Onboarding Call */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="relative z-10 flex flex-col items-center text-center"
            >
                <div className="w-16 h-16 xl:w-24 xl:h-24 bg-white rounded-full border border-blue-100 flex items-center justify-center mb-6 shadow-lg shadow-blue-100 relative group">
                    <div className="absolute inset-2 rounded-full border border-blue-100 border-dashed animate-[spin_10s_linear_infinite]" />
                    <Settings className="w-6 h-6 xl:w-10 xl:h-10 text-blue-600" />
                    <div className="absolute -top-2 -right-2 w-6 h-6 xl:w-8 xl:h-8 bg-blue-600 rounded-full flex items-center justify-center font-bold text-white border-4 border-white shadow-md text-sm xl:text-base">1</div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Onboarding Call</h3>
                <div className="flex items-center gap-2 text-sm text-blue-700 font-medium mb-3 bg-blue-50 px-3 py-1 rounded-full border border-blue-100">
                    <Clock className="w-3.5 h-3.5" /> 45 Minutes
                </div>
                <p className="text-slate-600 leading-relaxed max-w-xs text-sm">
                    We dive deep into your practice rules, scheduling preferences, and brand voice to configure your AI agents perfectly.
                </p>
            </motion.div>

            {/* Step 2: Go Live */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="relative z-10 flex flex-col items-center text-center"
            >
                <div className="w-16 h-16 xl:w-24 xl:h-24 bg-white rounded-full border border-indigo-100 flex items-center justify-center mb-6 shadow-lg shadow-indigo-100 relative group">
                    <div className="absolute inset-0 bg-indigo-50/50 rounded-full animate-pulse" />
                    <Zap className="w-6 h-6 xl:w-10 xl:h-10 text-indigo-600" />
                    <div className="absolute -top-2 -right-2 w-6 h-6 xl:w-8 xl:h-8 bg-indigo-600 rounded-full flex items-center justify-center font-bold text-white border-4 border-white shadow-md text-sm xl:text-base">2</div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Go Live</h3>
                <div className="flex items-center gap-2 text-sm text-indigo-700 font-medium mb-3 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-100">
                    <Rocket className="w-3.5 h-3.5" /> Within 24 Hours
                </div>
                <p className="text-slate-600 leading-relaxed max-w-xs text-sm">
                    Your Front Office AI agents are deployed and start handling calls and tasks immediately, providing instant relief.
                </p>
            </motion.div>

            {/* Step 3: Optimization */}
            <motion.div 
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="relative z-10 flex flex-col items-center text-center"
            >
                <div className="w-16 h-16 xl:w-24 xl:h-24 bg-white rounded-full border border-green-100 flex items-center justify-center mb-6 shadow-lg shadow-green-100 relative group">
                    <div className="absolute inset-0 bg-green-50/50 rounded-full" />
                    <Calendar className="w-6 h-6 xl:w-10 xl:h-10 text-green-600" />
                    <div className="absolute -top-2 -right-2 w-6 h-6 xl:w-8 xl:h-8 bg-green-600 rounded-full flex items-center justify-center font-bold text-white border-4 border-white shadow-md text-sm xl:text-base">3</div>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Fine Tuning</h3>
                <div className="flex items-center gap-2 text-sm text-green-700 font-medium mb-3 bg-green-50 px-3 py-1 rounded-full border border-green-100">
                    <Users className="w-3.5 h-3.5" /> 2x Weekly Check-ins
                </div>
                <p className="text-slate-600 leading-relaxed max-w-xs text-sm">
                    We conduct bi-weekly reviews to fine-tune agent behavior, ensuring they match your evolving preferences and drive positive ROI.
                </p>
            </motion.div>
        </div>

      </div>
    </div>
  );
};

export default FrontOfficeGettingStartedSlide;
